package com.nonopn.skydome.common.app.pages.dashboard

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.expandVertically
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.nonopn.skydome.common.app.api.model.DashboardImpactReportV2
import com.nonopn.skydome.common.app.api.model.DashboardImpactReportV2.DashboardImpactPeriod
import com.nonopn.skydome.common.app.api.model.DashboardImpactReportV2.PeriodReport
import com.nonopn.skydome.common.app.pages.dashboard.components.ButterflyReport
import com.nonopn.skydome.common.app.pages.dashboard.components.IntensitiesReport
import com.nonopn.skydome.common.app.pages.dashboard.components.MathReport
import com.nonopn.skydome.common.app.pages.dashboard.components.PolarityReport
import com.nonopn.skywatcher.design.theme.AppColor
import com.nonopn.skywatcher.design.utils.LocalFrame
import com.nonopn.skywatcher.design.utils.WindowType
import com.nonopn.skywatcher.design.widgets.DefaultCard
import korlibs.time.DateFormat
import korlibs.time.DateTimeTz
import org.jetbrains.compose.resources.stringResource
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.dashboard_header_today
import com.nonopn.skywatcher.common.app.res.dashboard_header_30days
import com.nonopn.skywatcher.common.app.res.dashboard_header_90days
import com.nonopn.skywatcher.common.app.res.dashboard_header_12months
import com.nonopn.skywatcher.common.app.res.dashboard_including_today
import com.nonopn.skywatcher.common.app.res.dashboard_impacts_cg

@Composable
fun DashboardReportPeriod(
    modifier: Modifier,
    impactPeriod: DashboardImpactPeriod,
    period: PeriodReport,
    includingToday: DashboardImpactReportV2.NegPosCount?
) {
    val isTablet = when (LocalFrame.current) {
        WindowType.SMARTPHONE_TINY -> false
        WindowType.SMARTPHONE -> false
        WindowType.PHABLET -> true
        WindowType.TABLET -> true
    }

    var isExpanded by remember { mutableStateOf(true) }

    DefaultCard(
        modifier = modifier.clip(
            shape = RoundedCornerShape(10.dp)
        ),
        backgroundColor = AppColor.White
    ) {
        Header(
            modifier = Modifier.fillMaxWidth(),
            title = when (impactPeriod) {
                DashboardImpactPeriod.Today -> stringResource(
                    Res.string.dashboard_header_today,
                    DateTimeTz.nowLocal().format(DateFormat("dd/MM/yyyy"))
                )
                DashboardImpactPeriod.Last30Days -> stringResource(Res.string.dashboard_header_30days)
                DashboardImpactPeriod.Last90Days -> stringResource(Res.string.dashboard_header_90days)
                DashboardImpactPeriod.Last12Years -> stringResource(Res.string.dashboard_header_12months)
            },
            subText = includingToday?.let {
                stringResource(Res.string.dashboard_including_today, it.total().toString())
            },
            background = impactPeriod.background,
            activity = period.count.total(),
            isExpanded = isExpanded,
        ) {
            isExpanded = !isExpanded
        }

        AnimatedVisibility(
            visible = isExpanded,
            enter = expandVertically() + fadeIn(),
            exit = shrinkVertically() + fadeOut()
        ) {
            Column(
                modifier = Modifier.padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp),
            ) {
                if (isTablet) {
                    Row(
                        modifier = Modifier.fillMaxWidth().height(IntrinsicSize.Min),
                        horizontalArrangement = Arrangement.spacedBy(16.dp),
                    ) {
                        PolarityReport(
                            modifier = Modifier.weight(0.25f).fillMaxHeight(),
                            negPosCount = period.count
                        )

                        IntensitiesReport(
                            modifier = Modifier.weight(0.45f).fillMaxHeight(),
                            intensities = period.intensities,
                            total = period.intensities.let {
                                DashboardImpactReportV2.NegPosCount(
                                    positive = it.values.sumOf { holder -> holder.positive },
                                    negative = it.values.sumOf { holder -> holder.negative },
                                )
                            }
                        )

                        MathReport(
                            modifier = Modifier.weight(0.30f).fillMaxHeight(),
                            mathReport = period.math
                        )
                    }
                } else {
                    Column(
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        PolarityReport(
                            modifier = Modifier.fillMaxWidth(),
                            negPosCount = period.count
                        )

                        IntensitiesReport(
                            modifier = Modifier.fillMaxWidth(),
                            intensities = period.intensities,
                            total = period.intensities.let {
                                DashboardImpactReportV2.NegPosCount(
                                    positive = it.values.sumOf { holder -> holder.positive },
                                    negative = it.values.sumOf { holder -> holder.negative },
                                )
                            }
                        )

                        MathReport(
                            modifier = Modifier.fillMaxWidth(),
                            mathReport = period.math
                        )
                    }
                }

                period.butterFly?.let { rawMap ->
                    // Detect MM/yyyy format (12-month butterfly) vs DD/MM (30-day)
                    val isMonthlyFormat = rawMap.keys.firstOrNull()
                        ?.split("/")?.let { it.size == 2 && (it.getOrNull(1)?.length ?: 0) == 4 }
                        ?: false
                    val processedData = if (isMonthlyFormat) {
                        // Filter out current month, sort descending (most recent first)
                        val now = DateTimeTz.nowLocal()
                        val currentKey = now.format(DateFormat("MM/yyyy"))
                        rawMap.entries
                            .filter { it.key != currentKey }
                            .sortedWith { a, b ->
                                val pa = a.key.split("/")
                                val pb = b.key.split("/")
                                val sa = ((pa.getOrNull(1)?.toIntOrNull() ?: 0) * 100) +
                                    (pa[0].trimStart('0').toIntOrNull() ?: 0)
                                val sb = ((pb.getOrNull(1)?.toIntOrNull() ?: 0) * 100) +
                                    (pb[0].trimStart('0').toIntOrNull() ?: 0)
                                sb - sa
                            }
                            .associate { it.key to it.value }
                    } else {
                        // Sort 30-day entries descending (most recent day first, DD/MM format)
                        val now = DateTimeTz.nowLocal()
                        val currentMonth = now.month.index1
                        val currentYear = now.year.year
                        rawMap.entries
                            .sortedWith { a, b ->
                                fun score(key: String): Int {
                                    val parts = key.split("/")
                                    val day = parts.getOrNull(0)?.toIntOrNull() ?: 0
                                    val month = parts.getOrNull(1)?.toIntOrNull() ?: 0
                                    // month > currentMonth means it belongs to previous year (Jan/Dec crossover)
                                    val year = if (month <= currentMonth) currentYear else currentYear - 1
                                    return year * 10000 + month * 100 + day
                                }
                                score(b.key) - score(a.key)
                            }
                            .associate { it.key to it.value }
                    }
                    if (processedData.isNotEmpty()) {
                        val chartLabel = if (isMonthlyFormat) "📊 Graphique 12 mois" else "📊 Graphique 30 jours"
                        var isButterflyExpanded by remember { mutableStateOf(false) }
                        val allEntries = processedData.entries.toList()
                        val first3 = allEntries.take(3).associate { it.key to it.value }
                        val remaining = allEntries.drop(3).associate { it.key to it.value }
                        // Section label
                        Text(
                            text = chartLabel,
                            fontSize = 13.sp,
                            fontWeight = FontWeight.SemiBold,
                            color = AppColor.StrikeText.copy(alpha = 0.65f),
                            modifier = Modifier.padding(bottom = 4.dp)
                        )
                        // Always show first 3 rows
                        ButterflyReport(
                            modifier = Modifier.fillMaxWidth(),
                            data = first3
                        )
                        // Expandable remaining rows
                        if (remaining.isNotEmpty()) {
                            AnimatedVisibility(
                                visible = isButterflyExpanded,
                                enter = expandVertically() + fadeIn(),
                                exit = shrinkVertically() + fadeOut()
                            ) {
                                ButterflyReport(
                                    modifier = Modifier.fillMaxWidth(),
                                    data = remaining
                                )
                            }
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(horizontal = 12.dp, vertical = 6.dp),
                                horizontalArrangement = Arrangement.Center
                            ) {
                                Box(
                                    modifier = Modifier
                                        .clip(RoundedCornerShape(20.dp))
                                        .border(1.dp, AppColor.StrikePrimary, RoundedCornerShape(20.dp))
                                        .background(AppColor.StrikePrimary.copy(alpha = 0.08f))
                                        .clickable { isButterflyExpanded = !isButterflyExpanded }
                                        .padding(horizontal = 20.dp, vertical = 8.dp)
                                ) {
                                    Text(
                                        text = if (isButterflyExpanded) "▲ Voir moins" else "▼ Voir + ${remaining.size}",
                                        fontSize = 13.sp,
                                        color = AppColor.StrikePrimary,
                                        fontWeight = FontWeight.SemiBold
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun Header(
    modifier: Modifier,
    title: String,
    subText: String?,
    background: Brush,
    activity: Int,
    isExpanded: Boolean,
    onClick: () -> Unit,
) {
    Row(
        modifier = modifier.background(background)
            .clickable(
                enabled = true,
                onClick = onClick
            ).padding(8.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        Text(
            text = title,
            color = AppColor.White,
            fontWeight = FontWeight.Bold,
        )

        Spacer(modifier = Modifier.weight(1.0f))

        Column(
            modifier = Modifier
                .clip(RoundedCornerShape(percent = 50))
                .background(Color(0x66ffffff))
                .padding(horizontal = 6.dp, vertical = 2.dp)
        ) {
            Text(
                text = stringResource(Res.string.dashboard_impacts_cg, activity.toString()),
                color = AppColor.White,
                fontWeight = FontWeight.Bold
            )
        }

        subText?.let {
            Text(
                text = it,
                color = AppColor.White.copy(0.65f),
                fontSize = 12.sp,
            )
        }

        Text(
            text = when (isExpanded) {
                true -> "▼"
                false -> "▲"
            },
            color = AppColor.White.copy(0.65f),
        )
    }
}

@Composable
private fun ButterflyAccordionHeader(
    modifier: Modifier = Modifier,
    label: String,
    isExpanded: Boolean,
    onToggle: () -> Unit,
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .background(AppColor.StrikeCardBackground)
            .clickable { onToggle() }
            .padding(horizontal = 12.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        Text(
            text = label,
            fontSize = 14.sp,
            fontWeight = FontWeight.SemiBold,
            color = AppColor.StrikeText
        )
        Text(
            text = if (isExpanded) "▲" else "▼",
            fontSize = 13.sp,
            color = AppColor.StrikeText.copy(alpha = 0.55f)
        )
    }
}

@Composable
@Preview
private fun DashboardReportPeriodPreview() {
    DashboardReportPeriod(
        modifier = Modifier.width(600.dp).height(500.dp),
        impactPeriod = DashboardImpactPeriod.Today,
        period = PeriodReport(
            count = DashboardImpactReportV2.NegPosCount(100, 200),
            intensities = DashboardImpactReportV2.IntensityDistributions.entries.associateWith {
                DashboardImpactReportV2.NegPosCount(
                    100,
                    200
                )
            }
        ),
        includingToday = DashboardImpactReportV2.NegPosCount(0, 200),
    )
}